// File: src/contexts/CountryContext.js
// Context for managing country/region settings

import React, { createContext, useContext, useState, useEffect } from 'react';
import { countries as countryList, getCountry } from '../data/countries';

const CountryContext = createContext();

// The list itself lives in ../data/countries. Re-exported here because that is
// where every consumer already imports it from.
export { countries } from '../data/countries';

// Where a visitor lands, and what an unrecognised stored country falls back to.
// Named rather than "whatever is first in the array": the list is alphabetical
// within its groups now, so countries[0] is Aland, not Finland.
export const FALLBACK_COUNTRY = 'FI';

export function CountryProvider({ children }) {
  const [country, setCountryState] = useState(() => {
    // Validated, not just defaulted-if-absent. A stale or hand-edited
    // rcCountry used to leave `country` set to a code no longer in the list
    // while `currentCountry` fell back to Finland - so the picker showed
    // Finland while every request filtered on the dead code and came back
    // empty.
    const saved = localStorage.getItem('rcCountry');
    return getCountry(saved) ? saved : FALLBACK_COUNTRY;
  });

  useEffect(() => {
    localStorage.setItem('rcCountry', country);
  }, [country]);

  const changeCountry = (countryCode) => {
    setCountryState(countryCode);
  };

  const currentCountry = getCountry(country) || getCountry(FALLBACK_COUNTRY);

  return (
    <CountryContext.Provider
      value={{ country, currentCountry, changeCountry, countries: countryList }}
    >
      {children}
    </CountryContext.Provider>
  );
}

export function useCountry() {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within CountryProvider');
  }
  return context;
}
